import React, { useEffect, useState } from "react";
import { Table, Button, Upload, message, Popconfirm, Space, Card, Spin } from "antd";
import { UploadOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL || "";

interface Certificate {
  id: number;
  filename: string;
  url: string;
  created_at: string;
}

const CertificateAdmin: React.FC = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/certificate/`);
      setCertificates(res.data.certificates || []);
    } catch (error) {
      message.error("Ошибка при загрузке сертификатов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("certificate", file);

    setUploading(true);
    try {
      await axios.post(`${API_URL}/certificate/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.success("Сертификат успешно загружен");
      fetchCertificates();
    } catch (error) {
      message.error("Ошибка при загрузке сертификата");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/certificate/${id}`);
      message.success("Сертификат удален");
      fetchCertificates();
    } catch (error) {
      message.error("Ошибка при удалении");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
    },
    {
      title: "Имя файла",
      dataIndex: "filename",
    },
    {
      title: "Дата добавления",
      dataIndex: "created_at",
      render: (text: string) =>
        new Date(text).toLocaleString("ru-RU"),
    },
    {
      title: "Скачать",
      dataIndex: "url",
      render: (url: string, record: Certificate) =>
        <a href={url} target="_blank" rel="noopener noreferrer">
          Скачать
        </a>,
    },
    {
      title: "Действия",
      key: "actions",
      render: (_: any, record: Certificate) => (
        <Popconfirm
          title="Удалить этот сертификат?"
          onConfirm={() => handleDelete(record.id)}
          okText="Да"
          cancelText="Нет"
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      ),
      width: 100,
    },
  ];

  return (
    <Card title="Сертификаты" extra={
      <Space>
        <Upload
          showUploadList={false}
          beforeUpload={file => {
            handleUpload(file);
            return false; 
          }}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
            Загрузить
          </Button>
        </Upload>
        <Button icon={<ReloadOutlined />} onClick={fetchCertificates} />
      </Space>
    }>
      <Spin spinning={loading}>
        <Table
          dataSource={certificates}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Spin>
    </Card>
  );
};

export default CertificateAdmin;
